import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { fetchQuota } from "../lib/api";
import { useAuth } from "./AuthContext";
import type { QuotaSnapshot } from "../types";

interface QuotaContextValue {
  quota: QuotaSnapshot | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const QuotaContext = createContext<QuotaContextValue | undefined>(undefined);

export function QuotaProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [quota, setQuota] = useState<QuotaSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const activeRef = useRef(true);

  useEffect(
    () => () => {
      activeRef.current = false;
    },
    []
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const snapshot = await fetchQuota();
      if (activeRef.current) setQuota(snapshot);
    } catch (err) {
      console.error("Falha ao carregar a cota de pesquisas:", err);
      if (activeRef.current) setQuota(null);
    } finally {
      if (activeRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    if (!user) {
      setQuota(null);
      return;
    }

    async function load() {
      setIsLoading(true);
      try {
        const snapshot = await fetchQuota();
        if (active) setQuota(snapshot);
      } catch (err) {
        console.error("Falha ao carregar a cota de pesquisas:", err);
        if (active) setQuota(null);
      } finally {
        if (active) setIsLoading(false);
      }
    }
    load();

    return () => {
      active = false;
    };
  }, [user]);

  return (
    <QuotaContext.Provider value={{ quota, isLoading, refresh }}>
      {children}
    </QuotaContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useResearchQuota(): QuotaContextValue {
  const context = useContext(QuotaContext);
  if (context === undefined) {
    throw new Error("useResearchQuota must be used within a QuotaProvider");
  }
  return context;
}
